/**
 * POS Family & Friends Discount Component
 * Apply a 15% discount for family members (requires manager approval)
 * Tracks all F&F discounts for reporting
 */
import { useState, useEffect } from 'react';
import { 
  Users, Search, Loader2, X, Percent, CreditCard, Banknote,
  ShieldCheck, UserCheck, AlertCircle, Smartphone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

const FAMILY_FRIENDS_DISCOUNT = 15;

const PAYMENT_METHODS = [
  { id: 'cash', name: 'Cash', icon: Banknote },
  { id: 'card', name: 'Card', icon: CreditCard },
  { id: 'mpaisa_qr', name: 'M-PAiSA', icon: Smartphone }
];

export default function POSFamilyFriendsDiscount({ 
  cart, 
  subtotal, 
  staffId, 
  staffName,
  onClose, 
  onApplyDiscount 
}) {
  const [step, setStep] = useState('manager_auth'); // manager_auth, customer_info, confirm
  const [managerPin, setManagerPin] = useState('');
  const [managerData, setManagerData] = useState(null);
  const [verifyingManager, setVerifyingManager] = useState(false);
  
  const [customerName, setCustomerName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  
  const [processing, setProcessing] = useState(false);

  // Calculate discounted totals
  const discountAmount = subtotal * (FAMILY_FRIENDS_DISCOUNT / 100);
  const discountedSubtotal = subtotal - discountAmount;
  // Prices are VAT-inclusive, so extract VAT from discounted amount
  const vatAmount = discountedSubtotal - (discountedSubtotal / 1.125);
  const finalTotal = discountedSubtotal;

  // Verify manager PIN
  const verifyManager = async () => {
    if (!managerPin || managerPin.length < 4) {
      toast.error('Please enter a valid manager PIN');
      return;
    }

    setVerifyingManager(true);
    try {
      const res = await api.post('/api/pos/verify-manager', {
        pin: managerPin,
        staff_id: staffId
      });

      if (res.data.success && res.data.is_manager) {
        setManagerData(res.data);
        setStep('customer_info');
        toast.success(`Manager ${res.data.name} verified`);
      } else {
        toast.error('Invalid PIN or not a manager/supervisor');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Manager verification failed');
    } finally {
      setVerifyingManager(false);
    }
  };

  // Apply the F&F discount
  const applyDiscount = async () => {
    if (!customerName.trim()) {
      toast.error('Please enter customer name');
      return;
    }
    if (!relationship.trim()) {
      toast.error('Please specify relationship');
      return;
    }

    setProcessing(true);
    try {
      // Record the F&F discount for tracking
      const res = await api.post('/api/pos/family-friends-discount', {
        staff_id: staffId,
        staff_name: staffName,
        approving_manager_id: managerData?.id,
        approving_manager_name: managerData?.name,
        customer_name: customerName.trim(),
        relationship: relationship.trim(),
        customer_phone: customerPhone.trim() || null,
        original_subtotal: subtotal,
        discount_percent: FAMILY_FRIENDS_DISCOUNT,
        discount_amount: discountAmount,
        final_total: finalTotal,
        cart_items: cart.map(item => ({
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        notes: notes.trim() || null
      });

      if (res.data.success) {
        toast.success(`15% Family & Friends discount applied!`, {
          description: `Approved by ${managerData?.name}`
        });

        // Pass discount back to POS - ONLY pass percent for percentage discounts
        // DO NOT pass 'amount' to avoid double-counting (POSPage calculates it)
        onApplyDiscount({
          type: 'family_friends',
          percent: FAMILY_FRIENDS_DISCOUNT,
          // 'amount' is intentionally omitted - POSPage.jsx calculates it from percent
          approvedBy: managerData?.name,
          approvedById: managerData?.id,
          customerName: customerName,
          relationship: relationship,
          trackingId: res.data.tracking_id
        });

        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply discount');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="ff-discount-modal">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-rose-500 to-pink-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Family & Friends Discount</h2>
              <p className="text-sm text-white/80 flex items-center gap-1">
                <Percent className="w-3 h-3" />
                15% Discount (Manager Approval Required)
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/10">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6">
          {/* Step 1: Manager Authentication */}
          {step === 'manager_auth' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-8 h-8 text-rose-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Manager Authorization Required
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Enter manager or supervisor PIN to approve this discount
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Manager PIN
                </label>
                <Input
                  type="password"
                  placeholder="Enter 6-digit PIN"
                  value={managerPin}
                  onChange={(e) => setManagerPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={(e) => e.key === 'Enter' && verifyManager()}
                  className="h-14 text-2xl text-center tracking-[0.5em] font-mono"
                  autoFocus
                  data-testid="ff-manager-pin"
                />
              </div>

              <Button
                onClick={verifyManager}
                disabled={verifyingManager || managerPin.length < 4}
                className="w-full h-12 bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="ff-verify-manager-btn"
              >
                {verifyingManager ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <ShieldCheck className="w-5 h-5 mr-2" />
                    Verify Manager
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Step 2: Customer Information */}
          {step === 'customer_info' && (
            <div className="space-y-4">
              {/* Manager Verified Badge */}
              <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <UserCheck className="w-5 h-5 text-green-600" />
                <span className="text-green-700 dark:text-green-300 text-sm">
                  Approved by <strong>{managerData?.name}</strong> ({managerData?.role})
                </span>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g., John Smith"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="h-11"
                  data-testid="ff-customer-name"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  Relationship <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g., Brother, Sister, Parent, Friend"
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  className="h-11"
                  data-testid="ff-relationship"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  Phone (optional)
                </label>
                <Input
                  placeholder="For loyalty tracking"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="h-11"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                  Notes (optional)
                </label>
                <Input
                  placeholder="Any additional notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="h-11"
                />
              </div>

              {/* Order Summary */}
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Order Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600 dark:text-gray-400">
                    <span>Subtotal ({cart.length} items)</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-rose-600 font-medium">
                    <span>F&F Discount (15%)</span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span>Total</span>
                    <span>${finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setStep('manager_auth'); setManagerPin(''); setManagerData(null); }}
                  className="flex-1 h-12"
                >
                  Back
                </Button>
                <Button
                  onClick={applyDiscount}
                  disabled={processing || !customerName.trim() || !relationship.trim()}
                  className="flex-1 h-12 bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="ff-apply-discount-btn"
                >
                  {processing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Percent className="w-5 h-5 mr-2" />
                      Apply 15% Discount
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
